import { type DeviceType } from "@/components/filters/DeviceTypeFilter";
import {
  STORE_INSIGHTS_REPORT_SELECTION_SESSION_KEY,
  type StoreInsightsSharedDateWindowPeriod,
} from "@/pages/store-insights/shared/storeInsightsSelectionSession";
import { useStoreInsightsDeviceTypeFilter } from "@/pages/store-insights/shared/useStoreInsightsDeviceTypeFilter";
import { useStoreInsightsMultiStoreSelection } from "@/pages/store-insights/shared/useStoreInsightsMultiStoreSelection";
import { useStoreInsightsSharedDateWindow } from "@/pages/store-insights/shared/useStoreInsightsSharedDateWindow";

interface UseStoreInsightsReportFiltersOptions {
  enableDeviceFilter?: boolean;
  defaultDeviceType?: DeviceType;
  defaultPeriod?: StoreInsightsSharedDateWindowPeriod;
  availablePeriods?: readonly StoreInsightsSharedDateWindowPeriod[];
}

/**
 * Compatibility composer for store-insights reports. New non-report pages
 * should compose the selection, date-window, and device-filter primitives
 * directly so their page policy is visible at the call site.
 */
export const useStoreInsightsReportFilters = ({
  enableDeviceFilter = false,
  defaultDeviceType = "all",
  defaultPeriod = "yesterday",
  availablePeriods,
}: UseStoreInsightsReportFiltersOptions = {}) => {
  const selection = useStoreInsightsMultiStoreSelection({
    sessionKey: STORE_INSIGHTS_REPORT_SELECTION_SESSION_KEY,
    includeUngroupedGroups: true,
  });
  const dateWindow = useStoreInsightsSharedDateWindow({ defaultPeriod, availablePeriods });
  const deviceFilter = useStoreInsightsDeviceTypeFilter({
    enabled: enableDeviceFilter,
    defaultDeviceType,
    selectionSession: {
      kind: "multi",
      key: STORE_INSIGHTS_REPORT_SELECTION_SESSION_KEY,
    },
    selectionReady: !selection.loading && !selection.selectionLoading,
  });

  return {
    user: selection.user,
    isCraveUser: selection.isCraveUser,
    selectedStores: selection.selectedStores,
    loading: selection.loading,
    selectionLoading: selection.selectionLoading,
    period: dateWindow.period,
    customRange: dateWindow.customRange,
    currentDateRange: dateWindow.currentDateRange,
    deviceType: deviceFilter.deviceType,
    customers: selection.customers,
    selectedCustomerIds: selection.selectedCustomerIds,
    groups: selection.groups,
    selectedGroups: selection.selectedGroups,
    groupCounts: selection.groupCounts,
    filteredStores: selection.filteredStores,
    onCustomerChange: selection.onCustomerChange,
    onGroupsChange: selection.onGroupsChange,
    onStoresChange: selection.onStoresChange,
    onPeriodChange: dateWindow.onPeriodChange,
    onCustomRangeChange: dateWindow.onCustomRangeChange,
    onDeviceTypeChange: deviceFilter.onDeviceTypeChange,
    deviceTypeFilterDisabled: deviceFilter.deviceTypeFilterDisabled,
  };
};
